import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { ShieldIcon, FeelIcon } from './BespokeIcons';

/**
 * LiabilityDisclaimer - One-click agreement before starting the experience
 * Provides legal protection while remaining reasonable and user-friendly
 */
const LiabilityDisclaimer = ({ open, onAccept, onDecline }) => {
  const [agreed, setAgreed] = useState(false);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onDecline?.()}>
      <DialogContent 
        className="bg-[#0f1419] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="liability-disclaimer-dialog"
      >
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <ShieldIcon size={24} color="#FBBF24" />
            </div>
          </div>
          <DialogTitle 
            className="text-xl text-center text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Before We Begin
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Main disclaimer text */}
          <div className="bg-white/5 rounded-lg p-4 border border-white/10 text-sm text-white/70 leading-relaxed space-y-3">
            <p>
              <strong className="text-white">ElchmE</strong> is an interactive tool designed for 
              <strong className="text-amber-400"> educational and self-reflection purposes</strong>. 
              It is inspired by Nonviolent Communication (NVC) principles and is intended to support 
              personal growth and improved communication.
            </p>
            
            <p>
              <strong className="text-white">This tool is not a substitute for:</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 text-white/60 ml-2">
              <li>Professional therapy or counseling</li>
              <li>Medical or psychological treatment</li>
              <li>Legal advice or mediation services</li>
              <li>Crisis intervention or emergency services</li>
            </ul>

            <p>
              By using ElchmE, you acknowledge that all content is provided 
              <strong className="text-amber-400"> "as is" for informational and entertainment purposes only</strong>. 
              The creators, contributors, and affiliates of ElchmE, The Belonging Sessions, and 
              Defiance Springs assume no liability for any outcomes, decisions, or actions taken 
              based on your use of this tool.
            </p>

            <p className="text-white/50 text-xs">
              If you are experiencing a mental health crisis, please contact a qualified professional 
              or call your local emergency services immediately.
            </p>
          </div>

          {/* Agreement checkbox */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
            <Checkbox 
              id="agree" 
              checked={agreed} 
              onCheckedChange={setAgreed}
              className="mt-0.5 border-amber-500/50 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
              data-testid="liability-agree-checkbox"
            />
            <label 
              htmlFor="agree" 
              className="text-sm text-white/80 cursor-pointer leading-relaxed"
            >
              I understand and accept these terms. I take full responsibility for my use of this 
              tool and any outcomes that may result.
            </label>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onDecline}
              className="flex-1 border-white/20 text-white/60 hover:bg-white/5"
              data-testid="liability-decline-btn"
            >
              Not Now
            </Button>
            <Button
              onClick={onAccept}
              disabled={!agreed}
              className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="liability-accept-btn"
            >
              <FeelIcon size={16} color="#000000" className="mr-2" />
              I Understand
            </Button>
          </div>

          {/* Quick note */}
          <p className="text-center text-[10px] text-white/30 pt-2">
            This agreement is stored locally and you won't be asked again on this device.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Check if user has already accepted the disclaimer
 */
export const hasAcceptedDisclaimer = () => {
  return localStorage.getItem('elchme_disclaimer_accepted') === 'true';
};

/**
 * Mark disclaimer as accepted
 */
export const acceptDisclaimer = () => {
  localStorage.setItem('elchme_disclaimer_accepted', 'true');
  localStorage.setItem('elchme_disclaimer_accepted_at', new Date().toISOString());
};

export default LiabilityDisclaimer;
